
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';

import { sharedState } from '../../../../framework/state';
import { getResource, putResource } from '../../../../framework/client/resource';
import EmailField from '../../../../framework/fields/EmailField.vue';
import DateField from '../../../../framework/fields/DateField.vue';

import { NotificationRecipient } from '../../../../../common/application/model/NotificationRecipient';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import { SmartFleetTask } from '../../../../../common/application/model/SmartFleetTask';
import { errorToast, successToast } from '../../../../application/service/toast_service';
import { VehicleContract } from '../../../../../common/application/model/VehicleContract';
import {
    VehicleContractFillRequest,
    vehicleContractFillRequestEditableKeys,
} from '../../../../../common/application/model/VehicleContractFillRequest';
import TextField from '@/browser/framework/fields/TextField.vue';
import NumberField from '@/browser/framework/fields/NumberField.vue';
import SelectField from '@/browser/framework/fields/SelectField.vue';
import CurrencyAmountField from '@/browser/framework/fields/CurrencyAmountField.vue';
import PercentageField from '@/browser/framework/fields/PercentageField.vue';
import VehicleContractFillRequestFieldsPreview from '@/browser/application/components/manager/smart-fleet-task/VehicleContractFillRequestFieldsPreview.vue';

@Component({
    components: {
        VehicleContractFillRequestFieldsPreview,
        PercentageField,
        CurrencyAmountField,
        SelectField,
        NumberField,
        TextField,
        DateField,
        EmailField,
        ValidationObserver,
    },
})
export default class ConfirmCompleteTaskModal extends Vue {
    readonly resourceType = ApplicationResource.SMART_FLEET_TASK;

    @Prop(Object) readonly smartFleetTask!: SmartFleetTask;

    shared = sharedState;
    local = {
        loading: false,
        sending: false,
        notificationRecipients: [] as NotificationRecipient[],
        oneTimeEmail: '',
        smartFleetTask: {} as SmartFleetTask,
        completedOn: new Date().toString(),
        nextInspectionDate: null as null | string,
        vehicleContract: {} as VehicleContract,
        vehicleContractFillRequest: null as VehicleContractFillRequest | null, // only for isFillRequestMergingTask
    };

    async mounted() {
        this.local.smartFleetTask = { ...this.smartFleetTask };
        await this.loadAsyncData();
    }

    async markAsDone(skipFillRequestMerging: boolean = false): Promise<void> {
        try {
            if (this.isInspectionTask) {
                await putResource<VehicleContract>(
                    ApplicationResource.VEHICLE_CONTRACT,
                    this.local.vehicleContract.id,
                    {
                        ...this.local.vehicleContract,
                        lastInspectionDate: new Date(this.local.completedOn),
                        nextInspectionDate: this.local.nextInspectionDate
                            ? new Date(this.local.nextInspectionDate)
                            : null,
                    },
                );
                await putResource<SmartFleetTask>(this.resourceType, this.local.smartFleetTask.id, {
                    ...this.local.smartFleetTask,
                    completedOn: new Date(this.local.completedOn),
                    completed: true,
                });

                successToast(this, 'message.smartFleetTaskSaved');
                this.$emit('close');
            }

            if (this.isFillRequestMergingTask) {
                if (this.local.vehicleContractFillRequest) {
                    if (!skipFillRequestMerging) {
                        const update: Record<string, any> = {};

                        for (const key of Object.keys(vehicleContractFillRequestEditableKeys)) {
                            const keyValue =
                                this.local.vehicleContractFillRequest[key as keyof VehicleContractFillRequest];
                            if (keyValue !== null && keyValue !== undefined) {
                                update[key] = keyValue;
                            }
                        }
                        await putResource<VehicleContract>(
                            ApplicationResource.VEHICLE_CONTRACT,
                            this.local.vehicleContract.id,
                            {
                                ...this.local.vehicleContract,
                                ...update,
                            },
                        );
                    }
                    await putResource<VehicleContractFillRequest>(
                        ApplicationResource.VEHICLE_CONTRACT_FILL_REQUEST,
                        this.local.vehicleContractFillRequest.id,
                        {
                            ...this.local.vehicleContractFillRequest,
                            status: 'MERGED',
                        },
                    );
                }

                await putResource<SmartFleetTask>(this.resourceType, this.local.smartFleetTask.id, {
                    ...this.local.smartFleetTask,
                    completedOn: new Date(this.local.completedOn),
                    completed: true,
                });

                successToast(this, 'message.smartFleetTaskSaved');
                this.$emit('close');
            }
        } catch (error) {
            errorToast(this);
            this.$emit('close');
        }
    }

    get isInspectionTask(): boolean {
        return this.local.smartFleetTask.taskType === 'inspection';
    }
    get isFillRequestMergingTask(): boolean {
        return this.local.smartFleetTask.taskType === 'fill-request-merging';
    }

    get label(): string {
        if (this.isInspectionTask) {
            return `inspectionDate`;
        } else {
            return `completedOn`;
        }
    }

    private async loadAsyncData(): Promise<void> {
        this.local.loading = true;

        if (!this.local.smartFleetTask.vehicleContractId) {
            return;
        }

        this.local.vehicleContract = (await getResource<VehicleContract>(
            ApplicationResource.VEHICLE_CONTRACT,
            this.local.smartFleetTask.vehicleContractId,
        ))!!;

        if (this.isFillRequestMergingTask) {
            this.local.vehicleContractFillRequest =
                (await getResource<VehicleContractFillRequest>(
                    ApplicationResource.VEHICLE_CONTRACT_FILL_REQUEST,
                    this.local.smartFleetTask.additionalInfo.vehicleContractFillRequestId,
                )) ?? null;
        }

        this.local.loading = false;
    }
}
